/**
 * Pure snap utility for vertex move / vertex add in the UV floorplan view.
 * Given a cursor position and the two neighbor vertices adjacent to the
 * vertex being placed, returns a snapped position and metadata describing
 * which neighbors are snap-locked.
 *
 * Snap rule:
 *   - For each neighbor, find the nearest 45° direction from neighbor → cursor.
 *   - If the cursor's angular distance to that direction is within tolerance,
 *     the neighbor is "snap-eligible".
 *   - Both eligible → snap to intersection of the two rays (dual snap).
 *   - Only one eligible → project cursor onto that neighbor's ray (single snap).
 *   - Neither → freeform (cursor unchanged).
 *   - A neighbour is eligible only while the cursor is also within
 *     LINE_SNAP_TOLERANCE_M of its ray: the angle alone widens with the
 *     neighbour's distance.
 *   - shiftHeld disables snap entirely.
 */

import { LINE_SNAP_TOLERANCE_M } from './snapTolerances';

export const SNAP_ANGLE_STEP_RAD = Math.PI / 4;
export const VERTEX_ANGLE_SNAP_TOLERANCE_RAD = (5 * Math.PI) / 180;

const PARALLEL_EPS = 1e-6;
const COINCIDENT_EPS = 1e-9;

export const NO_SNAP = Object.freeze({
  type: 'none',
  prevSnapped: false,
  nextSnapped: false,
});

/**
 * @param {Object} params
 * @param {{x:number, z:number}} params.cursor
 * @param {{x:number, z:number}} params.prev - previous neighbor vertex
 * @param {{x:number, z:number}} params.next - next neighbor vertex
 * @param {boolean} [params.shiftHeld=false]
 * @param {number} [params.toleranceRad=VERTEX_ANGLE_SNAP_TOLERANCE_RAD]
 * @returns {{
 *   position: {x:number, z:number},
 *   snap: {
 *     type: 'none' | 'single' | 'dual',
 *     prevSnapped: boolean,
 *     nextSnapped: boolean,
 *   }
 * }}
 */
export function snapVertexToNeighborAngles({
  cursor,
  prev,
  next,
  shiftHeld = false,
  toleranceRad = VERTEX_ANGLE_SNAP_TOLERANCE_RAD,
}) {
  if (shiftHeld) {
    return {
      position: { x: cursor.x, z: cursor.z },
      snap: { ...NO_SNAP },
    };
  }

  const prevInfo = computeNeighborSnap(cursor, prev, toleranceRad);
  const nextInfo = computeNeighborSnap(cursor, next, toleranceRad);

  if (prevInfo.snapped && nextInfo.snapped) {
    const intersection = intersectRays(
      prev,
      prevInfo.targetAngle,
      next,
      nextInfo.targetAngle
    );
    if (intersection) {
      return {
        position: intersection,
        snap: { type: 'dual', prevSnapped: true, nextSnapped: true },
      };
    }
    // Parallel or behind-neighbor — fall back to the single snap with the
    // smaller angular delta.
    if (prevInfo.angularDelta <= nextInfo.angularDelta) {
      return {
        position: prevInfo.projection,
        snap: { type: 'single', prevSnapped: true, nextSnapped: false },
      };
    }
    return {
      position: nextInfo.projection,
      snap: { type: 'single', prevSnapped: false, nextSnapped: true },
    };
  }

  if (prevInfo.snapped) {
    return {
      position: prevInfo.projection,
      snap: { type: 'single', prevSnapped: true, nextSnapped: false },
    };
  }
  if (nextInfo.snapped) {
    return {
      position: nextInfo.projection,
      snap: { type: 'single', prevSnapped: false, nextSnapped: true },
    };
  }

  return {
    position: { x: cursor.x, z: cursor.z },
    snap: { ...NO_SNAP },
  };
}

/**
 * For a single neighbor, compute the nearest 45° snap target, the angular
 * delta, whether it's within tolerance, and the projection of the cursor
 * onto the snap ray.
 */
function computeNeighborSnap(cursor, neighbor, toleranceRad) {
  const dx = cursor.x - neighbor.x;
  const dz = cursor.z - neighbor.z;

  // Degenerate: cursor coincides with neighbor — angle is undefined.
  if (Math.hypot(dx, dz) < COINCIDENT_EPS) {
    return {
      snapped: false,
      targetAngle: 0,
      angularDelta: Infinity,
      projection: { x: neighbor.x, z: neighbor.z },
    };
  }

  const angle = Math.atan2(dz, dx);
  const targetAngle =
    Math.round(angle / SNAP_ANGLE_STEP_RAD) * SNAP_ANGLE_STEP_RAD;

  let delta = angle - targetAngle;
  while (delta > Math.PI) delta -= 2 * Math.PI;
  while (delta <= -Math.PI) delta += 2 * Math.PI;
  const angularDelta = Math.abs(delta);

  const cos = Math.cos(targetAngle);
  const sin = Math.sin(targetAngle);
  let t = dx * cos + dz * sin;
  if (t < 0) t = 0;
  // Off the ray by no more than a line snap's reach as well: an angle alone
  // grows with the distance to the neighbour, and on long walls the cone
  // swallowed every small move.
  const offRay = Math.abs(dx * -sin + dz * cos);

  return {
    snapped: angularDelta < toleranceRad && offRay <= LINE_SNAP_TOLERANCE_M,
    targetAngle,
    angularDelta,
    projection: { x: neighbor.x + t * cos, z: neighbor.z + t * sin },
  };
}

/**
 * Compute the snap position + info for the vertex at `vertexIndex` in a
 * floor polygon, based on its two adjacent neighbors.
 *
 * Wraps `snapVertexToNeighborAngles` with the neighbor-derivation logic that
 * both vertex-move and vertex-add need. Returns freeform (cursor unchanged,
 * NO_SNAP) when the polygon doesn't have at least 3 corners.
 *
 * @param {Array<{x:number, z:number}>} corners - floor polygon corners in order
 * @param {number} vertexIndex - index of the vertex being placed
 * @param {{x:number, z:number}} cursor
 * @param {boolean} [shiftHeld=false]
 * @returns {{
 *   position: {x:number, z:number},
 *   snap: { type: 'none' | 'single' | 'dual', prevSnapped: boolean, nextSnapped: boolean }
 * }}
 */
export function computeVertexSnap(
  corners,
  vertexIndex,
  cursor,
  shiftHeld = false
) {
  if (!corners || corners.length < 3) {
    return {
      position: { x: cursor.x, z: cursor.z },
      snap: { ...NO_SNAP },
    };
  }
  const n = corners.length;
  const prev = corners[(vertexIndex - 1 + n) % n];
  const next = corners[(vertexIndex + 1) % n];
  return snapVertexToNeighborAngles({
    cursor,
    prev: { x: prev.x, z: prev.z },
    next: { x: next.x, z: next.z },
    shiftHeld,
  });
}

/**
 * Single-anchor variant: snap cursor angle relative to one anchor (e.g. the
 * previous vertex during multi-click polygon drawing, where there is no
 * "next" neighbor yet). Mirrors the per-neighbor snap rule used by
 * `computeVertexSnap`.
 *
 * @param {Object} params
 * @param {{x:number, z:number}} params.cursor
 * @param {{x:number, z:number}} params.anchor
 * @param {boolean} [params.shiftHeld=false]
 * @param {number} [params.toleranceRad=VERTEX_ANGLE_SNAP_TOLERANCE_RAD]
 * @returns {{
 *   snapped: boolean,
 *   position: {x:number, z:number},
 *   targetAngle: number,
 * }}
 */
export function snapVertexToAnchorAngle({
  cursor,
  anchor,
  shiftHeld = false,
  toleranceRad = VERTEX_ANGLE_SNAP_TOLERANCE_RAD,
}) {
  if (shiftHeld) {
    return {
      snapped: false,
      position: { x: cursor.x, z: cursor.z },
      targetAngle: 0,
    };
  }
  const info = computeNeighborSnap(cursor, anchor, toleranceRad);
  return {
    snapped: info.snapped,
    position: info.snapped ? info.projection : { x: cursor.x, z: cursor.z },
    targetAngle: info.targetAngle,
  };
}

/**
 * Intersect two rays. Each ray originates at a neighbor and goes along the
 * given snap angle. Returns the intersection point, or null if the rays are
 * (near-)parallel or the intersection lies behind either neighbor.
 */
function intersectRays(n1, theta1, n2, theta2) {
  const c1 = Math.cos(theta1);
  const s1 = Math.sin(theta1);
  const c2 = Math.cos(theta2);
  const s2 = Math.sin(theta2);

  const det = Math.sin(theta1 - theta2);
  if (Math.abs(det) < PARALLEL_EPS) return null;

  const dx = n2.x - n1.x;
  const dz = n2.z - n1.z;

  const t1 = (c2 * dz - s2 * dx) / det;
  const t2 = (c1 * dz - s1 * dx) / det;

  if (t1 < 0 || t2 < 0) return null;

  return { x: n1.x + t1 * c1, z: n1.z + t1 * s1 };
}

// Coordinates this close are the same point: the snap's ray intersection
// lands on the original corner up to floating-point noise, not exactly on it.
const SAME_POINT_EPS_M = 1e-6;

/**
 * True when `position` is where the vertex at `vertexIndex` already is — a
 * placement that moves nothing, and so must write nothing and ask nothing.
 */
export function isVertexUnmoved(corners, vertexIndex, position) {
  const c = corners?.[vertexIndex];
  if (!c || !position) return false;
  return (
    Math.abs(c.x - position.x) < SAME_POINT_EPS_M &&
    Math.abs(c.z - position.z) < SAME_POINT_EPS_M
  );
}
